import { QueryClientProvider } from '@tanstack/react-query'
import { RouterProvider } from '@tanstack/react-router'
import { lazy, Suspense, useEffect, type ReactNode } from 'react'
import { queryClient } from './app/queryClient'
import { router } from './app/router'
import { BrandQueriesProvider, useMergedBrandShellAppearance } from '@/shared/api/brandQueries'
import { AppearanceProvider } from '@/shared/components/AppearanceProvider'
import { AppErrorBoundary } from '@/shared/components/AppErrorBoundary'
import { BrandCssVariables } from '@/shared/components/BrandCssVariables'
import { ImpersonationBanner } from '@/shared/components/ImpersonationBanner'
import { MaintenanceGate } from '@/shared/components/MaintenanceGate'
import { PaymentReturnBootstrap } from '@/shared/components/PaymentReturnBootstrap'
import { PaymentReturnNotifications } from '@/shared/components/PaymentReturnNotifications'
import { RateLimitNotifications } from '@/shared/components/RateLimitNotifications'
import { PwaUpdatePrompt } from '@/shared/components/PwaUpdatePrompt'
import { AnalyticsConsentSync } from '@/shared/components/legal/AnalyticsConsentSync'
import { SessionExpiredModal } from '@/shared/components/SessionExpiredModal'
import { trackPageview } from '@/shared/lib/analytics'
import { resolveThemedComponent, ThemeDevOverlay } from '@/shared/theme'

type ToastProviderProps = {
  children: ReactNode
}

const ThemedToastProvider = lazy(() =>
  resolveThemedComponent<ToastProviderProps>(
    () => import('@/shared/components/ToastProvider'),
    'shared/components/ToastProvider.tsx',
    'ToastProvider',
  ),
)

function AppShell() {
  const brand = useMergedBrandShellAppearance()

  useEffect(() => {
    trackPageview(router.state.location.pathname)

    return router.subscribe('onResolved', ({ toLocation, pathChanged }) => {
      if (pathChanged) {
        trackPageview(toLocation.pathname)
      }
    })
  }, [])

  return (
    <>
      <BrandCssVariables brand={brand} />
      <ImpersonationBanner />
      <AnalyticsConsentSync />
      <PaymentReturnBootstrap />
      <RouterProvider router={router} />
      <PaymentReturnNotifications />
      <RateLimitNotifications />
      <SessionExpiredModal />
      <MaintenanceGate />
      <PwaUpdatePrompt />
      <ThemeDevOverlay />
    </>
  )
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrandQueriesProvider>
        <AppearanceProvider>
          <AppErrorBoundary>
            <Suspense fallback={null}>
              <ThemedToastProvider>
                <AppShell />
              </ThemedToastProvider>
            </Suspense>
          </AppErrorBoundary>
        </AppearanceProvider>
      </BrandQueriesProvider>
    </QueryClientProvider>
  )
}

export default App
